import { queryOptions, type QueryClient } from '@tanstack/react-query';
import type { BoardColumn, ClientStatus, Priority, SprintState, WorkType } from '@rundown/types';
import { api } from '../../lib/api';

/**
 * Projects (delivery) transport — a project is a grouping only, with no
 * lifecycle of its own (v0.5 §1). This reads each project with the requests
 * grouped under it, which is what makes it a different screen from the
 * `projects` CRUD module's plain `{ id, name, archived }[]` list.
 *
 * ── ASSUMED CONTRACT ────────────────────────────────────────────────────────
 *   GET /workspaces/:workspaceId/delivery/projects?archived= -> DeliveryProject[]
 *
 * Query keys are local rather than added to lib/queryKeys.ts, which other
 * agents own concurrently while this milestone is built — see that file's own
 * HARD RULE comment for why every key here still starts
 * `['ws', workspaceId, …]` regardless of which file declares it.
 */

export interface DeliveryProjectRequest {
  readonly id: string;
  readonly number: number;
  readonly title: string;
  readonly type: WorkType;
  readonly priority: Priority;
  readonly status: ClientStatus;
  readonly approxHours: number;
  readonly isHotfix: boolean;
  readonly sprintId: string | null;
  readonly sprintName: string | null;
  readonly sprintState: SprintState | null;
  readonly sprintStartsOn: string | null;
  readonly sprintEndsOn: string | null;
  readonly closedAt: string | null;
  readonly createdAt: string;
}

export interface InternalDeliveryProjectRequest extends DeliveryProjectRequest {
  readonly boardColumn: BoardColumn;
  readonly visibleToClient: boolean;
  readonly ownerName: string | null;
}

/** `boardColumn` exists only on the internal projection, the same tell
 * `isInternalRequest` uses in features/requests/api.ts. */
export function isInternalProjectRequest(
  request: DeliveryProjectRequest,
): request is InternalDeliveryProjectRequest {
  return 'boardColumn' in request;
}

export interface DeliveryProject {
  readonly id: string;
  readonly name: string;
  readonly archived: boolean;
  /** First lock of a sprint holding one of its parts; null until work starts. */
  readonly startedAt: string | null;
  readonly changesAfterStart: number;
  readonly changeHoursAfterStart: number;
  readonly requests: readonly DeliveryProjectRequest[];
}

function deliveryProjectsPath(workspaceId: string): string {
  return `/workspaces/${encodeURIComponent(workspaceId)}/delivery/projects`;
}

export function fetchDeliveryProjects(workspaceId: string, signal?: AbortSignal): Promise<DeliveryProject[]> {
  return api.get<DeliveryProject[]>(deliveryProjectsPath(workspaceId), signal);
}

/** Every key here starts `['ws', workspaceId]` — see lib/queryKeys.ts's HARD
 * RULE, which this local set still follows even though it is not declared in
 * that shared file. */
export const deliveryProjectsKeys = {
  list: (workspaceId: string) => ['ws', workspaceId, 'delivery', 'projects'] as const,
};

export function deliveryProjectsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: deliveryProjectsKeys.list(workspaceId),
    queryFn: ({ signal }) => fetchDeliveryProjects(workspaceId, signal),
  });
}

// ---------------------------------------------------------------------------
// Writes — the vendor's side, under `project.manage`
// ---------------------------------------------------------------------------

export interface ProjectSummary {
  readonly id: string;
  readonly name: string;
  readonly archived: boolean;
}

function projectsPath(workspaceId: string, id?: string): string {
  const base = `/workspaces/${encodeURIComponent(workspaceId)}/projects`;
  return id === undefined ? base : `${base}/${encodeURIComponent(id)}`;
}

export function createProject(workspaceId: string, name: string): Promise<ProjectSummary> {
  return api.post<ProjectSummary>(projectsPath(workspaceId), { name });
}

export function updateProject(
  workspaceId: string,
  id: string,
  patch: { readonly name?: string; readonly archived?: boolean },
): Promise<ProjectSummary> {
  return api.patch<ProjectSummary>(projectsPath(workspaceId, id), patch);
}

/** Active projects, for a picker. */
export function activeProjectsQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: ['ws', workspaceId, 'projects', 'active'] as const,
    queryFn: ({ signal }) => api.get<ProjectSummary[]>(`${projectsPath(workspaceId)}?archived=false`, signal),
    staleTime: 60_000,
  });
}

/** A project or its parts changed: every list that shows the grouping refreshes. */
export function invalidateProjects(queryClient: QueryClient, workspaceId: string): Promise<void> {
  return Promise.all(
    [
      ['ws', workspaceId, 'delivery', 'projects'],
      ['ws', workspaceId, 'projects'],
      ['ws', workspaceId, 'settings', 'projects'],
      ['ws', workspaceId, 'backlog'],
      ['ws', workspaceId, 'requests'],
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  ).then(() => undefined);
}
